import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  TextField,
  InputAdornment,
  Stack,
  alpha,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  ContentCopy,
  OpenInNew,
  Send as SendIcon,
  CreditCard,
  AccountBalanceWallet,
} from '@mui/icons-material';
import { GlassCard } from '../../components/v2';
import { gradients } from '../../theme/v2Theme';
import QRCode from 'react-qr-code';
import { useParams } from 'react-router-dom';
import {
  isHandleAvailable,
  isSubHandleAvailable,
  getUserAddresses,
} from '../../utils/handleManager';
import Coins from '../../assets/coins/coins.json';
import { ethers } from 'ethers';
import ReactGA from 'react-ga4';
import toast from 'react-hot-toast';
import * as firebase from 'firebase/app';
import 'firebase/database';
import { handleToEnsStyle } from '../../utils/ensFormatter';
import { text } from 'stream/consumers';
 

const coinGradients: { [key: string]: string } = {
  BTC: gradients.sunset,
  ETH: gradients.ocean,
  USDC: gradients.success,
  USDT: gradients.success,
  MATIC: gradients.lavender,
  SOL: gradients.primary,
  BNB: gradients.sunset,
};

export const SendPage: React.FC = () => {
  const { handle, coin, address: urlAddress } = useParams<{ handle?: string; coin: string; address?: string }>();
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(true);
  const [handleExists, setHandleExists] = useState(false);
  const [address, setAddress] = useState('');
  const [userAddresses, setUserAddresses] = useState<{ [key: string]: string }>({});
  const [web3Loading, setWeb3Loading] = useState(false);
  const [web3Error, setWeb3Error] = useState<string | null>(null);
  const [txHash, setTxHash] = useState<string | null>(null);

  // Check if this is a direct address payment (from /pay/:coin/:address)
  const isDirectAddress = !!urlAddress;

  const coins = Coins as { [key: string]: any };
  const coinKey = coin?.toUpperCase() || 'BTC';
  const coinInfo = coins[coinKey] || {};

  const coinData = {
    name: coinInfo.name || coinKey,
    symbol: coinKey,
    address: address,
    gradient: coinGradients[coinKey] || gradients.primary,
    icon: coinInfo.symbol || coinKey.charAt(0),
  };

  useEffect(() => {
    async function loadAddressData() {
      // If this is a direct address payment (from /pay/:coin/:address)
      if (isDirectAddress && urlAddress) {
        setAddress(urlAddress);
        setHandleExists(true); // Treat as valid
        setLoading(false);

        // Track page view with Google Analytics
        ReactGA.event('open_addrs_direct', {
          coin: coinKey,
          address: urlAddress,
        });
        return;
      }

      // Otherwise, handle-based payment
      if (!handle) {
        setLoading(false);
        return;
      }

      try {
        // Check if handle exists
        const isAvailable = await isHandleAvailable(handle);
        const isSubAvailable = await isSubHandleAvailable(handle);

        let exists = false;
        if (isAvailable) {
          if (!isSubAvailable) {
            exists = true;
          }
        } else {
          exists = true;
        }

        setHandleExists(exists);

        // Load addresses if handle exists
        if (exists) {
          const fetchedAddresses = await getUserAddresses(handle);
          setUserAddresses(fetchedAddresses);
          const coinAddress = fetchedAddresses[coinKey];
          if (coinAddress) {
            setAddress(coinAddress);

            // Track page view with Google Analytics
            ReactGA.event('open_addrs', {
              addrs: handle,
              coin: coinKey,
              address: coinAddress,
            });

            // Track payment request view - increment payment request count
            try {
              const handleRef = firebase.database().ref(`/handles/${handle}`);
              const handleSnap = await handleRef.once('value');
              if (handleSnap.exists()) {
                const uid = handleSnap.val();
                if (uid) {
                  // Increment payment requests
                  firebase.database().ref(`/stats/${uid}/paymentRequests`).transaction((current) => {
                    return (current || 0) + 1;
                  });
                }
              }
            } catch (trackError) {
              console.error('Error tracking payment request:', trackError);
            }
          }
        }
      } catch (error) {
        console.error('Error loading address:', error);
      } finally {
        setLoading(false);
      }
    }

    loadAddressData();
  }, [handle, coinKey, isDirectAddress, urlAddress]);

  // Check URL for amount parameter on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlAmount = urlParams.get('amount');
    if (urlAmount) {
      setAmount(urlAmount);
    }
  }, []);

  // Format payment URI based on coin type (from V1)
  const getPaymentUri = () => {
    if (!coinInfo.uri || !address) return '';

    let formattedUri = coinInfo.uri.replace('{address}', address);

    if (amount && parseFloat(amount) > 0) {
      const amt = amount;

      // Ethereum - convert to wei (18 decimals)
      if (coinKey === 'ETH') {
        let weiAmount = amt;
        if (amt.includes('.')) {
          weiAmount = amt.replace('.', '');
          const decimalPlaces = amt.split('.')[1]?.length || 0;
          weiAmount = weiAmount + '000000000000000000';
          weiAmount = weiAmount.substring(0, weiAmount.length - decimalPlaces + 1);
        } else {
          weiAmount = amt + '000000000000000000';
        }
        formattedUri = formattedUri + '?value=' + weiAmount;
      }
      // ERC20 tokens
      else if (coinInfo.ERC20 === true) {
        let tokenAmount = amt;
        // USDC uses 6 decimals
        if (coinKey === 'USDC' || coinKey === 'USDT') {
          if (amt.includes('.')) {
            tokenAmount = amt.replace('.', '');
            const decimalPlaces = amt.split('.')[1]?.length || 0;
            tokenAmount = tokenAmount + '000000';
            tokenAmount = tokenAmount.substring(0, tokenAmount.length - decimalPlaces + 1);
          } else {
            tokenAmount = amt + '000000';
          }
        }
        // Other ERC20 use 18 decimals
        else {
          if (amt.includes('.')) {
            tokenAmount = amt.replace('.', '');
            const decimalPlaces = amt.split('.')[1]?.length || 0;
            tokenAmount = tokenAmount + '000000000000000000';
            tokenAmount = tokenAmount.substring(0, tokenAmount.length - decimalPlaces + 1);
          } else {
            tokenAmount = amt + '000000000000000000';
          }
        }
        formattedUri = formattedUri + '&uint256=' + tokenAmount;
      }
      // Bitcoin and Bitcoin Cash
      else if (coinKey === 'BTC' || coinKey === 'BCH') {
        formattedUri = formattedUri + '?amount=' + amt;
      }
    }

    return formattedUri;
  };

  const paymentUri = getPaymentUri();

  const handleCopyAddress = () => {
    navigator.clipboard.writeText(coinData.address);
    toast.success('Address copied to clipboard');
  };

  const handleOpenWallet = () => {
    // Open wallet URI (works on mobile)
    if (paymentUri) {
      window.location.href = paymentUri;
    }
  };

  const handleShowFullQR = () => {
    // Copy the payment link
    if (paymentUri) {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Payment link copied to clipboard');
    }
  };

  // Coinbase Pay integration
  const handleCoinbasePay = () => {
    const network = coinInfo.ERC20 ? 'ethereum' : coinInfo.name?.toLowerCase() || 'bitcoin';
    let url = `https://pay.coinbase.com/buy/select-asset?appId=58a3fa2e-617f-4198-81e7-096f5e498c00&addresses={"${address}":["${network}"]}&assets=["${coinInfo.ticker}"]`;
    if (amount && parseFloat(amount) > 0) {
      url += `&presetCryptoAmount=${parseFloat(amount)}`;
    }
    window.location.href = url;
  };

  // Transak fiat-to-crypto integration
  const handleTransak = () => {
    let url = `https://global.transak.com?apiKey=46e5d2a3-6676-483e-82e4-ad542cf81fa2&defaultCryptoCurrency=${coinKey}&walletAddress=${address}`;
    if (amount && parseFloat(amount) > 0) {
      url += `&defaultCryptoAmount=${amount}`;
    }
    window.location.href = url;
  };

  // Web3 direct send (MetaMask)
  const handleWeb3Send = async () => {
    setWeb3Loading(true);
    setWeb3Error(null);
    setTxHash(null);
    try {
      if (!(window as any).ethereum) {
        setWeb3Error('Please install MetaMask to use this feature');
        setWeb3Loading(false);
        return;
      }
      await (window as any).ethereum.request({ method: 'eth_requestAccounts' });
      const provider = new ethers.providers.Web3Provider((window as any).ethereum);
      const signer = provider.getSigner();
      if (!amount || parseFloat(amount) <= 0) {
        setWeb3Error('Please enter a valid amount');
        setWeb3Loading(false);
        return;
      }
      let tx;
      if (coinKey === 'ETH') {
        tx = await signer.sendTransaction({ to: address, value: ethers.utils.parseEther(amount) });
      } else if (coinInfo.ERC20 === true) {
        const erc20Abi = [
          'function transfer(address to, uint256 amount) returns (bool)',
          'function decimals() view returns (uint8)',
        ];
        const contractAddress = coinInfo.uri.split(':')[1].split('/')[0];
        const contract = new ethers.Contract(contractAddress, erc20Abi, signer);
        let decimals = 18;
        if (coinKey === 'USDC' || coinKey === 'USDT') decimals = 6;
        const tokenAmount = ethers.utils.parseUnits(amount, decimals);
        tx = await contract.transfer(address, tokenAmount);
      } else {
        setWeb3Error('This coin is not supported for Web3 direct send');
        setWeb3Loading(false);
        return;
      }
      const receipt = await tx.wait();
      setTxHash(receipt.transactionHash);
      ReactGA.event('web3_send_success', {
        addrs: handle,
        coin: coinKey,
        amount: amount,
        txHash: receipt.transactionHash,
      });
    } catch (error: any) {
      console.error('Web3 send error:', error);
      setWeb3Error(error.message || 'Transaction failed. Please try again.');
      ReactGA.event('web3_send_error', {
        addrs: handle,
        coin: coinKey,
        error: error.message,
      });
    } finally {
      setWeb3Loading(false);
    }
  };

  // No auto-redirect in this branch (embed site default)

  // Post dynamic height to parent when inside an iframe
  useEffect(() => {
    try {
      if (window.self === window.top) return; // not embedded
    } catch {}
    const postSize = () => {
      const height = document.body.scrollHeight;
      try {
        window.parent.postMessage({ type: 'addrs-embed:height', height }, '*');
      } catch {}
    };
    postSize();
    const ro = new ResizeObserver(() => postSize());
    ro.observe(document.body);
    const i = setInterval(postSize, 1000);
    return () => {
      try { ro.disconnect(); } catch {}
      clearInterval(i);
    };
  }, []);

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          background: '#f8fafc',
        }}
      >
        <Stack spacing={2} alignItems="center">
          <CircularProgress size={60} />
          <Typography variant="h6" color="text.secondary">
            Loading payment details...
          </Typography>
        </Stack>
      </Box>
    );
  }

  // Handle not found or address not set
  if (!handleExists || !address) {
    return (
      <Box sx={{ background: 'transparent', p: 2 }}>
        <Container maxWidth="sm">
          <GlassCard sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h6" fontWeight={800} gutterBottom>
              {!handleExists ? 'Handle Not Found' : 'Address Not Set'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {!handleExists
                ? (handle ? `@${handle} doesn't exist` : 'Invalid payment link')
                : (handle ? `@${handle} hasn't set up a ${coinData.name} address yet` : 'Invalid payment link')}
            </Typography>
            <Button
              href="/"
              variant="contained"
              sx={{
                background: gradients.primary,
                '&:hover': { background: gradients.primary, opacity: 0.9 },
              }}
            >
              Create Your Profile
            </Button>
          </GlassCard>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ background: '#ffffff00', minHeight: '100vh', display: 'flex', alignItems: 'center', py: 2 }}>
      <Container maxWidth="sm" style={{textAlign: 'center'}}>
        <Box sx={{ background: '#fff', borderRadius: 3, overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
          {/* Header */}
          <Box sx={{
            background: coinData.gradient,
            p: 2.5,
            textAlign: 'center',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.1) 100%)',
            }
          }}>
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Typography variant="h5" fontWeight={800} sx={{ color: '#fff' }}>
                {handle ? `Send ${coinData.symbol} to @${handle}` : `Send ${coinData.symbol}`}
              </Typography>
            </Box>
          </Box>

          {/* Content */}
          <Box sx={{ p: 2.5 }}>
            <Stack spacing={2}>
              {/* QR Code */}
              <Box sx={{ textAlign: 'center' }}>
                <Box sx={{
                  display: 'inline-block',
                  p: 2,
                  background: '#fff',
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: alpha('#000', 0.08),
                  boxShadow: '0 2px 12px rgba(0,0,0,0.06)'
                }}>
                  <QRCode value={paymentUri} size={180} />
                </Box>
              </Box>

            {/* Address */}
            <Box>
              <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 1 }}>
                {coinData.symbol} Address
              </Typography>
              {/* ENS label for ETH/ERC20 handles */}
              {handle && (coinKey === 'ETH' || coinInfo.ERC20 === true) && (
                <Box
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    p: 1.25,
                    borderRadius: 2,
                    mb: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0px 2px 12px rgba(102, 126, 234, 0.2)',
                    transition: 'all 0.2s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      boxShadow: '0px 4px 16px rgba(102, 126, 234, 0.3)',
                      transform: 'translateY(-1px)',
                    }
                  }}
                  onClick={() => {
                    navigator.clipboard.writeText(handleToEnsStyle(handle));
                    toast.success('ENS name copied to clipboard');
                  }}
                >
                  <Typography variant="body2" fontWeight={700} sx={{ color: '#fff' }} style={{ textAlign: 'center' }}>
                    {handleToEnsStyle(handle)}
                  </Typography>
                  <Box sx={{
                    px: 1.25,
                    py: 0.4,
                    marginLeft: .5,
                    borderRadius: 1.25,
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    color: '#fff',
                    background: 'rgba(255, 255, 255, 0.25)',
                    letterSpacing: '0.5px'
                  }}>ENS</Box>
                </Box>
              )}
              <Box
                sx={{
                  background: alpha('#000', 0.04),
                  p: 1.5,
                  pl: 2,
                  borderRadius: 2,
                  fontFamily: 'monospace',
                  wordBreak: 'break-all',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1.5,
                  border: '1px solid',
                  borderColor: alpha('#000', 0.08),
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    background: alpha('#000', 0.06),
                    borderColor: alpha('#000', 0.12),
                  },
                }}
              >
                <Typography variant="body2" sx={{ flex: 1, fontSize: '0.875rem', fontWeight: 500 }}>
                  {coinData.address}
                </Typography>
                <Tooltip title="Copy address">
                  <IconButton
                    onClick={handleCopyAddress}
                    size="small"
                    sx={{
                      '&:hover': {
                        background: alpha('#000', 0.08),
                      }
                    }}
                  >
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* Amount */}
            <Box>
              <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 1 }}>
                Amount (Optional)
              </Typography>
              <TextField
                fullWidth
                type="number"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography fontWeight={700} color="text.secondary">
                        {coinData.symbol}
                      </Typography>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    borderRadius: 2,
                    '&:hover': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: alpha('#000', 0.2),
                      }
                    },
                    '&.Mui-focused': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderWidth: 2,
                      }
                    }
                  },
                }}
              />
            </Box>

            <Divider sx={{ my: 0.5 }} />

            {/* Payment Methods */}
            <Box>
              <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 1 }}>
                Payment Methods
              </Typography>
              <Stack spacing={1.25}>
                {(coinKey === 'ETH' || coinInfo.ERC20 === true) && (
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleWeb3Send}
                    disabled={web3Loading || !amount}
                    sx={{
                      background: gradients.secondary,
                      py: 1.25,
                      borderRadius: 2,
                      fontWeight: 700,
                      fontSize: '0.95rem',
                      textTransform: 'none',
                      boxShadow: '0 2px 12px rgba(99, 102, 241, 0.25)',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        background: gradients.secondary,
                        opacity: 0.9,
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 16px rgba(99, 102, 241, 0.35)',
                      },
                      '&:disabled': {
                        background: alpha('#cccccc', 0.5),
                      }
                    }}
                    startIcon={web3Loading ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                  >
                    {web3Loading ? 'Sending...' : 'Send Directly (MetaMask)'}
                  </Button>
                )}
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleOpenWallet}
                  sx={{
                    background: gradients.primary,
                    py: 1.25,
                    borderRadius: 2,
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    textTransform: 'none',
                    boxShadow: '0 2px 12px rgba(99, 102, 241, 0.25)',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      background: gradients.primary,
                      opacity: 0.9,
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 16px rgba(99, 102, 241, 0.35)',
                    }
                  }}
                  startIcon={<AccountBalanceWallet />}
                  endIcon={<OpenInNew />}
                >
                  Open in Wallet
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleCoinbasePay}
                  startIcon={<CreditCard />}
                  endIcon={<OpenInNew />}
                  sx={{
                    py: 1.25,
                    borderRadius: 2,
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    textTransform: 'none',
                    borderWidth: 2,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderWidth: 2,
                      transform: 'translateY(-1px)',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    }
                  }}
                >
                  Buy with Card (Coinbase)
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleTransak}
                  startIcon={<CreditCard />}
                  endIcon={<OpenInNew />}
                  sx={{
                    py: 1.25,
                    borderRadius: 2,
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    textTransform: 'none',
                    borderWidth: 2,
                    borderColor: 'warning.main',
                    color: 'warning.main',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderWidth: 2,
                      borderColor: 'warning.main',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 2px 8px rgba(245, 158, 11, 0.15)',
                    }
                  }}
                >
                  Buy with Card (Transak)
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleShowFullQR}
                  startIcon={<ContentCopy />}
                  sx={{
                    py: 1.25,
                    borderRadius: 2,
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    textTransform: 'none',
                    borderWidth: 2,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderWidth: 2,
                      transform: 'translateY(-1px)',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    }
                  }}
                >
                  Copy Payment URI
                </Button>
              </Stack>
            </Box>

            {/* Alerts */}
            {web3Error && (
              <Alert
                severity="error"
                onClose={() => setWeb3Error(null)}
                sx={{
                  borderRadius: 2,
                  fontWeight: 600,
                  '& .MuiAlert-icon': {
                    fontSize: '1.5rem'
                  }
                }}
              >
                {web3Error}
              </Alert>
            )}
            {txHash && (
              <Alert
                severity="success"
                sx={{
                  borderRadius: 2,
                  fontWeight: 600,
                  '& .MuiAlert-icon': {
                    fontSize: '1.5rem'
                  }
                }}
              >
                Transaction successful!{' '}
                <a
                  href={`https://etherscan.io/tx/${txHash}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'inherit', textDecoration: 'underline', fontWeight: 700 }}
                >
                  View on Etherscan
                </a>
              </Alert>
            )}
          </Stack>
        </Box>
        </Box>
      </Container>
    </Box>
  );
};
